import { EditOutlined } from "@ant-design/icons"
import { Alert, Button, Card, Flex, Typography } from "antd"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { isRowCommitEnter } from "../../../../components/EntityTable/editable-row-cells.ts"
import { cn } from "../../../../utils/cn.ts"
import { LeadMissingFieldsNotice } from "../missing/LeadMissingFieldsNotice.tsx"
import { useLeadMissingCardFields } from "../missing/lead-missing-fields-context.tsx"
import { useWorkspaceCardEdit } from "./use-workspace-card-edit.ts"

const { Title } = Typography

interface WorkspaceCardEditBody<V extends object> {
  values: V
  setValues: (patch: Partial<V>) => void
  saving: boolean
}

interface WorkspaceCardViewBody {
  startEdit: () => void
}

const NOTHING_TO_SAVE = () => Promise.resolve(true)

interface WorkspaceCardProps<V extends object> {
  cardId: string
  title: ReactNode
  /**
   * Plain-text title for the ✎ button's accessible name, when `title` is a
   * rich node (e.g. an icon + label) rather than a bare string. Falls back to
   * `title` itself, so every existing plain-string caller is unaffected.
   */
  ariaTitle?: string
  /** Escape hatch for a caller that needs equal-height siblings in a grid row. */
  className?: string
  canEdit?: boolean
  extra?: ReactNode
  /**
   * Plain content, or a render function for a view that needs to open edit
   * mode itself (e.g. an inline "+ Add" shortcut next to an empty list) —
   * without exposing the edit state to every other, unrelated caller.
   */
  children: ReactNode | ((view: WorkspaceCardViewBody) => ReactNode)
  /** Omitted by a read-only card, which then never offers the ✎ affordance. */
  values?: V
  /** `"cancelled"` keeps the draft open and raises no failed-save alert. */
  onSave?: (changed: Partial<V>) => Promise<boolean | "cancelled">
  /** Gates Save so a card the user cannot fix from the response never posts. */
  validate?: (values: V) => boolean
  renderEdit?: (body: WorkspaceCardEditBody<V>) => ReactNode
  /** Rendered beside Cancel/Save (e.g. "Last saved 2 minutes ago") instead of
   * a disruptive confirmation — only meaningful while `renderEdit` is shown. */
  saveHint?: ReactNode
  /** Fires on every path that abandons the draft (Cancel, and the
   * unsaved-changes guard's Discard) — for undoing a mid-edit side effect
   * like an attachment upload that would otherwise be orphaned. */
  onCancel?: () => void
}

function WorkspaceCard<V extends object>({
  cardId,
  title,
  ariaTitle,
  className,
  values,
  onSave,
  validate,
  canEdit,
  extra,
  children,
  renderEdit,
  saveHint,
  onCancel
}: WorkspaceCardProps<V>) {
  const { t } = useTranslation()
  const edit = useWorkspaceCardEdit({
    cardId,
    values: values ?? ({} as V),
    onSave: onSave ?? NOTHING_TO_SAVE,
    onAbandon: onCancel
  })
  const editable = !!canEdit && !!renderEdit
  const invalid = !!validate && !validate(edit.values)
  const missing = useLeadMissingCardFields(cardId)

  // Standing rule: every field-entry screen with a Save/Apply action commits
  // on Enter. Reuses the inline-edit-row predicate (button/select/date-picker
  // excluded, textarea deliberately not — see its own comment) since the
  // rule is identical here, just over a card instead of a table row.
  const onEditKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (edit.saving || invalid || !isRowCommitEnter(event)) return
    event.preventDefault()
    void edit.save()
  }

  return (
    <Card
      size={"small"}
      className={cn(!!missing.length && "border-error", className)}
      data-testid={`workspace-card-${cardId}`}
    >
      <Flex vertical gap={"small"} className={"w-full h-full"}>
        <Flex wrap gap={"small"} align={"center"} justify={"space-between"}>
          <Title level={5} className={"m-0"}>
            {title}
          </Title>
          <Flex gap={"small"} align={"center"}>
            {extra}
            {editable && !edit.editing && (
              <Button
                size={"small"}
                type={"text"}
                aria-label={t("leadWorkspace.card.edit", {
                  title: ariaTitle ?? title
                })}
                icon={<EditOutlined />}
                onClick={edit.startEdit}
              />
            )}
          </Flex>
        </Flex>

        <LeadMissingFieldsNotice
          fields={missing}
          testId={`workspace-card-missing-${cardId}`}
        />

        {edit.error && (
          <Alert type={"error"} showIcon message={edit.error} role={"alert"} />
        )}

        {edit.editing && renderEdit ? (
          <Flex vertical gap={"small"} onKeyDown={onEditKeyDown}>
            {renderEdit({
              values: edit.values,
              setValues: edit.setValues,
              saving: edit.saving
            })}
            <Flex
              gap={"small"}
              align={"center"}
              justify={saveHint ? "space-between" : "end"}
            >
              {saveHint}
              <Flex gap={"small"}>
                <Button
                  size={"small"}
                  disabled={edit.saving}
                  onClick={edit.cancel}
                >
                  {t("leadWorkspace.card.cancel")}
                </Button>
                <Button
                  size={"small"}
                  type={"primary"}
                  loading={edit.saving}
                  disabled={edit.saving || invalid}
                  data-testid={`workspace-card-${cardId}-save`}
                  onClick={edit.save}
                >
                  {t("leadWorkspace.card.save")}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        ) : typeof children === "function" ? (
          children({ startEdit: edit.startEdit })
        ) : (
          children
        )}
      </Flex>
    </Card>
  )
}

export { type WorkspaceCardProps, type WorkspaceCardViewBody, WorkspaceCard }
